import React, { useState, useEffect } from 'react';
import { VisionboardImage } from '../types';
import { getVisionboardImages } from '../firebase/visionboard.service';

interface VisionboardMiniProps {
  goalId: string;
  ownerId: string;
  onExpand?: () => void; // Callback to open full visionboard
}

/**
 * VisionboardMini - Compact horizontal thumbnail strip
 * 
 * Shows miniature visionboard images in a horizontal row
 * Clicking expands to full visionboard
 */
const VisionboardMini: React.FC<VisionboardMiniProps> = ({
  goalId,
  ownerId,
  onExpand,
}) => {
  const [images, setImages] = useState<VisionboardImage[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hoveredImage, setHoveredImage] = useState<VisionboardImage | null>(null);

  // Load images
  useEffect(() => {
    const loadImages = async () => {
      console.log('[VisionboardMini] Loading images for goalId:', goalId, 'ownerId:', ownerId);
      setIsLoading(true);
      try {
        const data = await getVisionboardImages(ownerId, goalId);
        console.log('[VisionboardMini] Loaded images:', data.length, data);
        setImages(data);
      } catch (err) {
        console.error('[VisionboardMini] Failed to load:', err);
      } finally {
        setIsLoading(false);
      }
    };
    loadImages();
  }, [goalId, ownerId]);

  // Don't render if no images
  if (isLoading) {
    console.log('[VisionboardMini] Still loading...');
    return (
      <div className="flex items-center gap-2 h-14 bg-orange-50 rounded-lg p-1">
        <div className="w-12 h-12 bg-orange-100 rounded-lg animate-pulse" />
        <div className="w-12 h-12 bg-orange-100 rounded-lg animate-pulse" />
        <div className="w-12 h-12 bg-orange-100 rounded-lg animate-pulse" />
        <span className="text-xs text-orange-400 ml-2">Laden...</span>
      </div>
    );
  }

  if (images.length === 0) {
    console.log('[VisionboardMini] No images found for goalId:', goalId);
    // Tijdelijke debug indicator
    return (
      <div className="text-xs text-gray-400 py-1">
        [Debug: 0 visionboard images voor dit doel]
      </div>
    );
  }

  console.log('[VisionboardMini] Rendering', images.length, 'thumbnails');

  return (
    <div className="relative mb-2">
      {/* Thumbnails row */}
      <div 
        className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-thin scrollbar-thumb-gray-200 cursor-pointer"
        onClick={onExpand}
        title="Klik om visionboard te openen"
      >
        {images.slice(0, 6).map((image, index) => (
          <div
            key={image.id}
            className="flex-shrink-0 w-14 h-14 rounded-lg overflow-hidden border-2 border-gray-200 shadow-sm hover:border-orange-400 transition-colors"
            onMouseEnter={() => setHoveredImage(image)}
            onMouseLeave={() => setHoveredImage(null)}
          >
            <img
              src={image.imageUrl}
              alt={image.caption || `Visionboard ${index + 1}`}
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </div>
        ))}
        
        {/* Show count if more than 6 */}
        {images.length > 6 && (
          <div className="flex-shrink-0 w-14 h-14 rounded-lg bg-gray-100 border-2 border-gray-200 flex items-center justify-center text-sm font-medium text-gray-500 hover:border-orange-300 transition-colors">
            +{images.length - 6}
          </div>
        )}
      </div>

      {/* Hover preview - expands below thumbnails */}
      {hoveredImage && (
        <div className="mt-2 animate-in fade-in slide-in-from-top-2 duration-200">
          <div className="inline-block rounded-xl overflow-hidden border-4 border-orange-400 shadow-2xl bg-white">
            <img
              src={hoveredImage.imageUrl}
              alt={hoveredImage.caption || 'Visionboard preview'}
              className="max-w-xs max-h-64 object-contain"
              style={{ minWidth: '200px', minHeight: '150px' }}
            />
            {hoveredImage.caption && (
              <div className="bg-gray-900 text-white text-sm p-3">
                {hoveredImage.caption}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default VisionboardMini;
export { VisionboardMini };
